import * as React from "react";
import { ComboBox, Dropdown, IComboBox, IComboBoxOption, IDropdownOption, ResponsiveMode } from "@fluentui/react";
import { ChoiceProps } from "../../interfaces/props/ChoiceProps";
import { logger } from "../../../../utilities/logger";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { appendCustomOptions, resolveChoiceKey, resolveChoiceKeys, toChoiceList } from "../helpers/choiceOptionsHelper";

const getValueAsArray = (value: any): any[] => {
  return Array.isArray(value) ? value : value ? [value] : [];
}

const optionKeys = (options: IDropdownOption[]): any[] => options.map(option => option.key);

const sameEntries = (a: any[] | undefined, b: any[] | undefined): boolean =>
  a === b || (!!a && !!b && a.length === b.length && a.every((entry, i) => entry === b[i]));

const getOptions = (allowFreeForm: boolean, defaultOptions: any[], inputValue: any) => {
  // Choices normally arrive deduped from convertMetadataToFields; deduping again here is cheap
  // insurance, because a duplicate key breaks Fluent's first-match selection.
  const choices: any[] = toChoiceList(defaultOptions);
  const options = choices.map(item => ({ key: item, text: item }));
  return allowFreeForm ? appendCustomOptions(options, inputValue) : options;
}

export const CheckboxMultiColumn: React.FunctionComponent<ChoiceProps> = ({
  displayName,
  isRequired,
  Choices,
  inputValidator,
  updateInputValidationStatus,
  error,
  inputValue
}: ChoiceProps) => {
  const [allowFreeForm] = React.useState<boolean>(inputValue?.xml?.FillInChoice === SharePointMetadataBooleans.true);
  // Raw stored entries. Only the keys handed to the control are resolved, so an entry that differs
  // from a choice by surrounding whitespace renders as checked without being rewritten until the
  // user toggles something.
  const [selectedKeys, setSelectedKeys] = React.useState<string[]>(() => (inputValue?.value ? getValueAsArray(inputValue.value) : undefined));
  const [options, setOptions] = React.useState<any[]>(() => getOptions(allowFreeForm, Choices, inputValue?.value));
  // Memoised: Fluent compares this prop by identity inside an effect, so a fresh array on every
  // parent render would cost an extra internal render of the control per field.
  const resolvedSelectedKeys = React.useMemo(
    () => (selectedKeys ? resolveChoiceKeys(selectedKeys, optionKeys(options)) : undefined),
    [selectedKeys, options]
  );

  const isFirstRun = React.useRef(true);

  React.useEffect(() => {
    const value = inputValue?.value;
    // Follow the parent: one instance can be re-used for another item (the multi-file panel swaps
    // files on one form), so a value that becomes empty clears the selection and a stored entry
    // the list does not define becomes a custom entry, exactly as at mount.
    setSelectedKeys(prev => {
      const next = value ? getValueAsArray(value) : (prev ? [] : prev);
      return sameEntries(prev, next) ? prev : next;
    });
    if (allowFreeForm) {
      setOptions(prev => appendCustomOptions(prev, value));
    }
  }, [inputValue?.value]);

  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      updateInputValidationStatus(
        inputValidator.validate(inputValue?.value
          ? getValueAsArray(inputValue?.value)
          : null)
      );
    } catch (e) {
      logger(e, "checboxMultiColumn (useEffect 2)")
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const toggleSelection = React.useCallback(
    (key: string, selected: boolean): void => {
      const current = selectedKeys || [];
      const keys = optionKeys(options);
      // A toggle is the user acting on this field, so the whole reported array is normalised to
      // option keys (entries matching no option stay as they are) and duplicates collapse with it.
      const selection = resolveChoiceKeys(selected
        ? [...current, key]
        : current.filter(storedKey => resolveChoiceKey(storedKey, keys) !== key), keys);
      setSelectedKeys(selection);
      updateInputValidationStatus(inputValidator.validate(selection));
    },
    [selectedKeys, options, inputValidator, updateInputValidationStatus]
  );

  const onChange = React.useCallback(
    (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
      if (item) {
        toggleSelection(item.key as string, !!item.selected);
      }
    },
    [toggleSelection]
  );

  const onComboChange = React.useCallback(
    (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string): void => {
      let selected = option?.selected;
      if (!option && !value?.trim()) return;

      if (!option && value) {
        // Free-form text: Fluent found no option whose text starts with it (its own matching is
        // case-insensitive but never trimmed). Resolve it exactly like a stored value, so a
        // whitespace or case variant of an existing option toggles that option, as Fluent does for
        // a match of its own. Only genuinely new text becomes a custom entry, trimmed so it can
        // never seed a whitespace variant of its own.
        const keys = optionKeys(options);
        const resolved = resolveChoiceKey(value, keys);
        if (keys.includes(resolved)) {
          option = options.find(existing => existing.key === resolved);
          selected = !resolveChoiceKeys(selectedKeys, keys).includes(resolved);
        }
        else {
          const custom = value.trim();
          selected = true;
          option = { key: custom, text: custom };
          setOptions(prevOptions => [...prevOptions, option]);
        }
      }

      if (option) {
        toggleSelection(option.key as string, !!selected);
      }
    },
    [toggleSelection, options, selectedKeys]
  );

  const onBlur = (event): void => {
    updateInputValidationStatus(inputValidator.validate(selectedKeys));
  };

  return allowFreeForm
    ?
    <ComboBox
      multiSelect
      allowFreeform
      useComboBoxAsMenuWidth
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={resolvedSelectedKeys}
      label={displayName}
      options={options}
      onChange={onComboChange}
      required={isRequired()}
      errorMessage={error}
    />
    :
    <Dropdown
      // responsiveMode={ResponsiveMode.small}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      placeholder="Select"
      label={displayName}
      selectedKeys={resolvedSelectedKeys}
      onChange={onChange}
      onBlur={onBlur}
      multiSelect
      required={isRequired()}
      options={options}
      errorMessage={error}
      className="xb-sp-input"
    />;
};

export default CheckboxMultiColumn;
